// Invilus: slim mobile sticky add-to-cart bar.
// The bar never owns cart logic — it forwards its click to Dawn's real
// add-to-cart button so variant handling, validation and the cart
// drawer all keep working unchanged. It reveals itself once the main
// add-to-cart button leaves the viewport.
if (!customElements.get('invilus-sticky-cart')) {
  customElements.define(
    'invilus-sticky-cart',
    class InvilusStickyCart extends HTMLElement {
      connectedCallback() {
        // Guard against double-initialisation (Shopify theme editor).
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.priceContainer = document.getElementById('price-' + this.sectionId);
        this.stickyButton = this.querySelector('[data-sticky-add]');
        this.stickyButtonText = this.querySelector('[data-sticky-add-text]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');

        // Without a main button there is nothing to mirror — stay hidden.
        if (!this.mainButton || !this.stickyButton) return;

        // Forward the click to the real add-to-cart button.
        this.stickyButton.addEventListener('click', () => {
          if (this.mainButton.hasAttribute('disabled')) return;
          this.mainButton.click();
        });

        this.syncButton();
        this.syncPrice();

        // Keep the sticky button label/disabled state aligned with the
        // main button (sold-out, variant changes).
        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        // Mirror Dawn's #price-{sectionId} block — Dawn rewrites its
        // innerHTML on every variant change, so observing the subtree
        // is the most robust way to stay in sync without depending on
        // Dawn's internal PubSub module.
        if (this.priceContainer && this.stickyPrice) {
          this.priceObserver = new MutationObserver(() => this.syncPrice());
          this.priceObserver.observe(this.priceContainer, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        // Reveal the bar only while the main button is out of view.
        this.visibilityObserver = new IntersectionObserver(
          (entries) => this.setVisible(!entries[0].isIntersecting),
          { rootMargin: '0px 0px -80px 0px' }
        );
        this.visibilityObserver.observe(this.mainButton);
      }

      disconnectedCallback() {
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        // Make sure we never leave the body padding pinned when the
        // element is torn down (theme editor, section reload).
        document.documentElement.classList.remove('invilus-sticky-cart-active');
      }

      setVisible(visible) {
        this.classList.toggle('invilus-sticky-cart--visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        // `inert` keeps the off-screen button out of the tab order.
        this.inert = !visible;
        // Mark the document while the bar is visible so we can offset
        // the body's bottom padding on mobile (see invilus-sticky-cart.css).
        document.documentElement.classList.toggle(
          'invilus-sticky-cart-active',
          visible
        );
      }

      syncButton() {
        this.stickyButton.disabled = this.mainButton.hasAttribute('disabled');
        const label = this.mainButton.querySelector('span');
        if (label && this.stickyButtonText) {
          this.stickyButtonText.textContent = label.textContent.trim();
        }
      }

      syncPrice() {
        if (!this.priceContainer || !this.stickyPrice) return;
        const priceEl = this.priceContainer.querySelector('.price');
        if (!priceEl) return;
        // Show ONLY the amount the customer actually pays. Dawn renders both
        // .price__regular and .price__sale into the DOM and toggles visibility
        // via the .price--on-sale class. On sale, .price__sale ALSO holds the
        // struck compare-at price (which is HIGHER); the visually-hidden a11y
        // labels ("Regular price"/"Sale price") live there too. Reading the
        // whole block would make the slim text bar show the pre-discount price
        // and look more expensive than the product is. So target the single
        // price item the customer pays.
        const onSale = priceEl.classList.contains('price--on-sale');
        const priceItem = onSale
          ? priceEl.querySelector('.price__sale .price-item--sale')
          : priceEl.querySelector('.price__regular .price-item--regular');
        if (!priceItem) return;
        const next = priceItem.textContent.replace(/\s+/g, ' ').trim();
        if (next && next !== this.stickyPrice.textContent.trim()) {
          this.stickyPrice.textContent = next;
        }
      }
    }
  );
}
